import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/lib/api-client";
import type { User } from "@/api/generated/client/models/User";
import type { CompanyInvite } from "@/api/generated/client/models/CompanyInvite";

export interface InviteUserForm {
  email: string;
  role: "admin" | "member";
}

export function useTeamManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Fetch current user to get companyId
  const { data: currentUser } = useQuery({
    queryKey: ["/api/users/me"],
    queryFn: async (): Promise<User> => {
      return await apiClient.users.getCurrentUser() as User;
    },
  });

  // Fetch company members using new invite endpoint
  const { data: membersData, isLoading: isLoadingMembers } = useQuery({
    queryKey: ["/api/companies/:companyId/members", currentUser?.companyId],
    queryFn: async () => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.listCompanyMembers({ 
        companyId: currentUser.companyId 
      });
    },
    enabled: !!currentUser?.companyId,
  });

  // Fetch pending invites
  const { data: invitesData, isLoading: isLoadingInvites } = useQuery({
    queryKey: ["/api/companies/:companyId/invites", currentUser?.companyId],
    queryFn: async (): Promise<{ invites: CompanyInvite[] }> => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.listPendingInvites({ 
        companyId: currentUser.companyId 
      }) as { invites: CompanyInvite[] };
    },
    enabled: !!currentUser?.companyId && (currentUser?.role === "admin" || currentUser?.role === "owner"),
  });

  // Invite user mutation
  const inviteMutation = useMutation({
    mutationFn: async (form: InviteUserForm) => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.inviteMember({
        companyId: currentUser.companyId,
        inviteMemberRequest: {
          email: form.email,
          role: form.role,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/invites", currentUser?.companyId] });
      toast({
        title: "Invitation Sent",
        description: "An invitation email has been sent to the user.",
      });
      setInviteDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Invite Failed",
        description: error.message || "Failed to send invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Remove user mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId: number) => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.removeMember({
        companyId: currentUser.companyId,
        userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/members", currentUser?.companyId] });
      toast({
        title: "Member Removed",
        description: "The member has been removed from your company.",
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Remove Failed",
        description: error.message || "Failed to remove member. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: "admin" | "member" }) => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.updateMemberRole({
        companyId: currentUser.companyId,
        userId,
        updateMemberRoleRequest: { role },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/members", currentUser?.companyId] });
      toast({
        title: "Role Updated",
        description: "Member role has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update member role. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Resend invitation mutation
  const resendInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.resendInvitation({
        companyId: currentUser.companyId,
        inviteId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/invites", currentUser?.companyId] });
      toast({
        title: "Invitation Resent",
        description: "A new invitation email has been sent to the user.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Resend Failed",
        description: error.message || "Failed to resend invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Cancel invitation mutation
  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      if (!currentUser?.companyId) {
        throw new Error("No company ID");
      }
      return await apiClient.companies.cancelInvitation({
        companyId: currentUser.companyId,
        inviteId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/invites", currentUser?.companyId] });
      toast({
        title: "Invitation Cancelled",
        description: "The invitation has been cancelled successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Cancel Failed",
        description: error.message || "Failed to cancel invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Handlers
  const handleInviteSubmit = (form: InviteUserForm) => {
    inviteMutation.mutate(form);
  };

  const handleDeleteUser = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const handleRoleChange = (userId: number, newRole: string) => {
    if (newRole === "admin" || newRole === "member") {
      updateRoleMutation.mutate({ userId, role: newRole });
    }
  };

  const handleResendInvite = (inviteId: number) => {
    resendInviteMutation.mutate(inviteId);
  };

  const handleCancelInvite = (inviteId: number) => {
    cancelInviteMutation.mutate(inviteId);
  };

  const openDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const openInviteDialog = () => setInviteDialogOpen(true);
  const closeInviteDialog = () => setInviteDialogOpen(false);

  // Permission checks
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "owner";
  
  const canManageUser = (user: User) => {
    // Owner can manage anyone except themselves
    if (currentUser?.role === "owner") {
      return user.id !== currentUser.id;
    }
    // Admin can manage members but not owners or other admins
    if (currentUser?.role === "admin") {
      return user.role === "member" && user.id !== currentUser.id;
    }
    return false;
  };

  return {
    // Data
    currentUser,
    teamMembers: membersData?.members || [],
    pendingInvites: invitesData?.invites || [],
    isLoading: isLoadingMembers || isLoadingInvites,
    hasCompany: !!currentUser?.companyId,
    
    // Dialog state
    inviteDialogOpen,
    deleteDialogOpen,
    userToDelete,
    
    // Mutation states
    isInviting: inviteMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdatingRole: updateRoleMutation.isPending,
    isResending: resendInviteMutation.isPending,
    isCancelling: cancelInviteMutation.isPending,
    
    // Permissions
    isAdmin,
    canManageUser,
    
    // Actions
    handleInviteSubmit,
    handleDeleteUser,
    handleRoleChange,
    handleResendInvite,
    handleCancelInvite,
    openDeleteDialog,
    closeDeleteDialog,
    openInviteDialog,
    closeInviteDialog,
  };
}
